//import React from 'react';
import PropTypes from 'prop-types';

import * as math from '_core/utils/math';
import * as convert from '_core/utils/convert';
//import * as langs from 'utils/langs';
import { timeout } from '_core/utils/async';
import * as cache from '_core/utils/cache';
import * as http from '_core/utils/http';

import {
  API_SERVER,
  API_REST_URI,
  API_REQUEST_URI,
  API_UPLOAD_URI,
  API_LISTEN_METHOD,
  API_LISTEN_REQUIRED,
  API_LISTEN_INTERVALS,
  API_NOTICE,
  API_ERROR
} from 'config';


const METHODS_PROPS = {
  'client/visitor/authorize': { requestCacheInterval: 100 },
  'client/visitor/info': { requestCacheInterval: 100 },
  'client/visitor/logout': { requestCacheInterval: 100 }
};


const apiServer = convert.toString(API_SERVER).replace(/\/$/, '');
const SOFT_UNAUTHORIZED_PATTERNS = [
  /client\/visitor\/info/,
  /client\/verification\/request\/status/,
  /delivery\/options/
];
const VERIFICATION_STATUS_PATTERN = /client\/verification\/request\/status/;

const isSoftUnauthorized = (requestPath = '') => SOFT_UNAUTHORIZED_PATTERNS.some((pattern) => pattern.test(requestPath));
const buildUnauthorizedNoticeData = (requestPath = '') => ({
  soft: isSoftUnauthorized(requestPath),
  requestPath
});

let apiListenStart = false;
let listenVisibilityHandler = null;


export class ApiError extends Error {
  constructor(code, message, data, options) {
    const { silentConsole = false, softUnauthorized = false, ...errorOptions } = options || {};
    
    // В Error.message кладём строку (например, message3 или json.message),
    // чтобы не ломать стандартное поведение
    const safeMessage =
      typeof message === 'object' && message !== null
        ? message.message1 || ''
         : message;

    super(safeMessage, errorOptions);

    if (!code) code = 'system-error';

    this.code = code;
    this.data = data;
    this.softUnauthorized = !!softUnauthorized;
    // Сохраняем оригинальный объект/строку как есть
    this.rawMessage = message;

    if (!silentConsole) {
      if (data && Object.keys(data).length) {
        console.warn(code, '-', message, ':', data);
      } else {
        console.warn(code, '-', message);
      }
    }
  }
}


export const propTypes = {
  requestServer: PropTypes.string.isRequired,
  requestDelay: PropTypes.number.isRequired,
  
  attemptCount: PropTypes.number.isRequired,
  attemptInterval: PropTypes.number.isRequired,
  
  expectedCode: PropTypes.string
};

export const defaultProps = {
  requestServer: apiServer,
  requestDelay: 0,
  requestCacheInterval: 5000,
  requestFromServer: false,
  
  attemptCount: 10,
  attemptInterval: 2000,
  
  expectedCode: 'ok'
};

export const path = (uri) => {
  if (!uri)
    return null;
  
  if (typeof uri === 'object') {
    if (uri.id === 0)
      return null;
    
    uri = uri.path;
  }
  
  if (uri[0] !== '/')
    uri = '/' + uri;
  
  return apiServer + uri;
};

export const rest = async (type, method, data, props) => {
  props = buildProps(defaultProps, props);
  
  type = convert.toString(type).toUpperCase();
  
  let url = props.requestServer + API_REST_URI + method;
  
  const param = {
    method: type,
    mode: 'cors',
    headers: {}
  };
  
  if (type === 'GET') {
    if (data)
      url = http.param(url, data);
  } else {
    param.headers['Content-Type'] = 'application/json; charset=UTF-8';
    param.body = JSON.stringify(data);
  }
  
  if (props.requestFromServer) {
    if (typeof Window !== 'undefined')
      throw new ApiError(502, 'Requests from the website are not allowed, url: "' + url + '"', null);
  } else {
    if (typeof Window === 'undefined')
      throw new ApiError(502, 'Requests from the server are not allowed, url: "' + url + '"', null);
  }
  
  if (props.requestDelay)
    await timeout(props.requestDelay);
  
  if (param.mode === 'cors' && props.requestServer)
    param.credentials = 'include';
  
  let resp;
  
  for (let count = props.attemptCount - 1; ; count--) {
    try {
      resp = await fetch(url, param);
      
      if (resp.status >= 500) {
        if (!count)
          throw new ApiError(resp.status, resp.statusText, null);
        
        await timeout(props.attemptInterval);
        
        continue;
      }
      
      break;
    } catch (e) {
      if (!count) {
        if (e instanceof ApiError)
          throw e;
        
        throw new ApiError(502, 'Error while fetching data at "' + url + '": ' + e.message, null, { cause: e });
      }
      
      await timeout(props.attemptInterval);
    }
  }
  
  let json;
  
  try {
    json = await resp.json();
  } catch (e) {
    throw new ApiError(502, 'Invalid JSON format in response: ' + e.message, null, { cause: e });
  }
  
  if (resp.status >= 400) {
    if (resp.status === 401)
      notify(API_NOTICE.VISITOR_LOGOUT, buildUnauthorizedNoticeData(url));
    
    throw new ApiError(resp.status, json.error || (resp.status + ' ' + resp.statusText));
  }
  
  return json;
};

export const request = async (method, param, props) => {
  const fetchRequest = { param: param || {} };
  
  if (props?.lang)
    fetchRequest.lang = props.lang.id || props.lang;
  
  const fetchParam = {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
    },
    
    body: 'data=' + encodeURIComponent(JSON.stringify(fetchRequest))
  };
  
  const cache = request.cache || (request.cache = {});
  
  if (cache[method]?.body !== fetchParam.body || cache[method]?.iteration !== props?.iteration) {
    if (cache[method])
      clearTimeout(cache[method].timeout);
    
    cache[method] = {
      method,
      body: fetchParam.body, 
      promise: fetchData(API_REQUEST_URI.replace('%', method), fetchParam, props),
      timeout: setTimeout(() => delete cache[method], props?.requestCacheInterval || METHODS_PROPS[method] || defaultProps.requestCacheInterval),
      iteration: props?.iteration
    };
  }
  
  listen(props);
  
  return (await cache[method].promise).data;
};

export const upload = async (files, param, props) => {
  if (!Array.isArray(files))
    files = [files];
  
  if (!param)
    param = {};
  
  if (!param.category)
    param.category = 'ticket';
  
  const fetchParam = {
    method: 'POST',
    mode: 'cors',
    body: new FormData()
  };
  
  files.forEach((file) => fetchParam.body.append('file', file));
  
  Object.entries(param).forEach(([key, value]) => Array.isArray(value) ? value.forEach((item) => fetchParam.body.append(key, item)) : fetchParam.body.append(key, value));
  
  return (await fetchData(API_UPLOAD_URI, fetchParam, props)).list;
};

let listenVisitorId = 0
let listenFetchFirst = null;

const lastBalanceNoticeState = {};

function balanceNoticeKey(data) {
  if (!data || (data.currency_id == null && data.account_type_id == null))
    return null;
  return `${data.currency_id ?? ''}_${data.account_type_id ?? ''}`;
}

function isNewerBalanceChange(data) {
  const key = balanceNoticeKey(data);
  if (key == null) return true;
  const last = lastBalanceNoticeState[key];
  if (data.transaction_id != null) {
    const prevId = last?.transactionId ?? -Infinity;
    return data.transaction_id > prevId;
  }
  const time = data.time != null ? (typeof data.time === 'number' ? data.time : new Date(data.time).getTime()) : 0;
  const lastTime = last?.time ?? 0;
  return time > lastTime;
}

function applyBalanceNoticeState(data) {
  const key = balanceNoticeKey(data);
  if (key == null) return;
  const time = data.time != null ? (typeof data.time === 'number' ? data.time : new Date(data.time).getTime()) : 0;
  lastBalanceNoticeState[key] = {
    transactionId: data.transaction_id ?? lastBalanceNoticeState[key]?.transactionId,
    time: time || lastBalanceNoticeState[key]?.time,
  };
}

export const setListenVisitorId = (id) => {
  if (listenVisitorId !== id) {
    listenVisitorId = id;
    listenFetchFirst = null;
  }
};

export const listen = (props) => {
  if (apiListenStart || !API_LISTEN_REQUIRED || typeof window === 'undefined')
    return;
  
  apiListenStart = true;
  
  const isTabVisible = () => typeof document === 'undefined' || document.visibilityState === 'visible';
  
  const fetchProps = {
    ...props,
    
    attemptCount: 1,
    expectedCode: null
  };
  
  let fetchLast = 0;
  let fetchTimeout = null;
  let activeLast = Date.now();
  let lastVisitorId = 0;
  
  const fetchFunc = () => {
    fetchTimeout = null;
    
    if (!isTabVisible()) {
      activeLast = 0;
      fetchTimeout = setTimeout(fetchFunc, API_LISTEN_INTERVALS.inactive);
      return;
    }
    
    if (Date.now() - activeLast > API_LISTEN_INTERVALS.inactiveTimeout)
      activeLast = 0;
    
    const visitorId = lastVisitorId;
    
    lastVisitorId = listenVisitorId;
    
    rest('get', API_LISTEN_METHOD + '/' + encodeURIComponent(visitorId) + '/' + encodeURIComponent(fetchLast), null, fetchProps)
      .then((data) => {
        const count = data.list?.length;
        
        if (count) {
          fetchTimeout = setTimeout(fetchFunc, activeLast ? API_LISTEN_INTERVALS.activeAccel : API_LISTEN_INTERVALS.inactiveAccel);
        } else {
          fetchTimeout = setTimeout(fetchFunc, activeLast ? API_LISTEN_INTERVALS.active : API_LISTEN_INTERVALS.inactive);
        }
        
        if (data.time)
          fetchLast = data.time;
        
        if (listenFetchFirst !== visitorId) {
          listenFetchFirst = visitorId;
          
          return;
        }
        
        for (var n = 0; n < count; n++) {
          const name = data.list[n]?.name;
          const itemData = data.list[n]?.data || {};
          if (name === API_NOTICE.VISITOR_BALANCE) {
            if (!isNewerBalanceChange(itemData)) continue;
            applyBalanceNoticeState(itemData);
          }
          notify(name, itemData);
        }
        
        activeLast = Date.now();
      })
      .catch((error) => fetchTimeout = setTimeout(fetchFunc, activeLast ? API_LISTEN_INTERVALS.activeError : API_LISTEN_INTERVALS.inactiveError));
  };
  
  const activeFunc = () => {
    if (!activeLast && fetchTimeout) {
      clearTimeout(fetchTimeout);
      
      fetchTimeout = setTimeout(fetchFunc, API_LISTEN_INTERVALS.active);
    }
    
    activeLast = Date.now();
  };
  
  fetchTimeout = setTimeout(fetchFunc, API_LISTEN_INTERVALS.first);
  
  window.addEventListener('focus', activeFunc);
  window.addEventListener('click', activeFunc);
  window.addEventListener('mousemove', activeFunc);
  window.addEventListener('keydown', activeFunc);
  
  if (typeof document !== 'undefined') {
    const visibilityFunc = () => {
      if (isTabVisible())
        activeFunc();
      else
        activeLast = 0;
    };
    
    if (listenVisibilityHandler)
      document.removeEventListener('visibilitychange', listenVisibilityHandler);
    
    listenVisibilityHandler = visibilityFunc;
    document.addEventListener('visibilitychange', listenVisibilityHandler);
  }
};

export const notify = (name, data) => name && typeof window !== 'undefined' ? window.dispatchEvent(new CustomEvent('notify', { detail: { name, data: data || {} } })) : null;

export const getCached = (method, func, param, clearMillis, requestProps) => cache.get(
  null,
  [method, func, param],
  (context, reduce) => rest(method, func, param, requestProps),
  clearMillis
);

export const getMultipaged = (func, param, stats, clearMillis) => {
  if (Array.isArray(param)) {
    return Promise.all(param.map((id) => getMultipaged(func, id, stats))).then((results) => param.map((id, index) => results[index][id]));
  } else if (math.isObject(param)) {
    return rest('get', stats ? func + '/stats' : func, param);
  } else {
    return cache.get(
        null,
        [func],
        (context, reduce) => rest('post', http.param(func, stats), reduce).then((data) => data?.reduce((result, value, index) => (result[reduce[index]] = value, result), {})),
        clearMillis, // clearMillis
        param, // reduceKey
        100 // reduceMillis
      );
  }
};

// address

export const getAddressLocalitySuggest = (lang, query, limit) => {
  lang = lang === 'ru' ? 'ru' : 'en';
  
  return rest('get', http.param('address/locality/suggest', { lang, query, limit }));
};

export const getAddressLocality = (lang, id) => {
  lang = lang === 'ru' ? 'ru' : 'en';
  
  return cache.get(
    null,
    ['address/locality/suggest', lang],
    (context, reduce) => rest('post', http.param('address/locality/suggest', { lang }), reduce).then((data) => data && data.reduce((result, value, index) => { result[reduce[index]] = value; return result; }, {})),
    10 * 60000, // clearMillis
    id, // reduceKey
    100 // reduceMillis
  );
};

// client

export const getClientConfig = () => getCached('get', 'client/config', null, 30 * 60000);

export const getClientTokenGenerate = (visitorId) => getData(['client/token/generate', visitorId], null, 30 * 60000).then((data) => data.token);

export const getClientLangString = (code) => rest('get', 'client/lang/string/' + code);

export const clearLangCache = () => {
  if (request.cache && request.cache['client/lang/list']) {
    const cacheEntry = request.cache['client/lang/list'];
    if (cacheEntry.timeout) {
      clearTimeout(cacheEntry.timeout);
    }
    delete request.cache['client/lang/list'];
  }
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    const channel = new MessageChannel();
    channel.port1.onmessage = (event) => {
      if (event.data && event.data.success) {
        console.log('[API] Language cache cleared in service worker');
      }
    };
    navigator.serviceWorker.controller.postMessage(
      { type: 'CLEAR_LANG_CACHE' },
      [channel.port2]
    );
  }
};

export const clearHtmlCache = () => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    const channel = new MessageChannel();
    channel.port1.onmessage = (event) => {
      if (event.data && event.data.success) {
        console.log('[API] HTML cache cleared in service worker');
      }
    };
    navigator.serviceWorker.controller.postMessage(
      { type: 'CLEAR_HTML_CACHE' },
      [channel.port2]
    );
  }
};

export const getClientVisitor = (param, stats) => getMultipaged('client/visitor', param, stats, 10 * 60000);

export const getClientVisitorStatusTypes = () => getList(['client/visitor/status/types'], null);

export const getClientVisitorProtection = () => rest('get', 'client/visitor/protection');

export const getClientTagType = () => getCached('get', 'client/tag/type', null, 30 * 60000);

export const getClientTag = () => getCached('get', 'client/tag', null, 30 * 60000);

export const getClientTagEx = async () => {
  const tagTypes = await getClientTagType();
  const tags = await getClientTag();
  
  return tagTypes
    .filter((tagType) => tagType.is_goods)
    .map((tagType) => ({ ...tagType, tags: tags.filter((tag) => tag.type === tagType.id) }))
    .filter((tagType) => tagType.tags.length);
};

// classic

export const getClassicPeriod = () => getCached('get', 'classic/period', null, 30 * 60000);

export const getClassicVisitor = (param, stats) => rest('get', stats ? 'classic/visitor/stats' : 'classic/visitor', param);

export const getClassicVisitorColumn = () => getCached('get', 'classic/visitor/column', null, 30 * 60000);

export const getClassicVisitorDetails = (param, stats) => rest('get', stats ? 'classic/visitor/details/stats' : 'classic/visitor/details', param);

export const getClassicVisitorStatus = () => getCached('get', 'classic/visitor/status', null, 30 * 60000);

export const getClassicVisitorStatusType = () => getCached('get', 'classic/visitor/status/type', null, 30 * 60000);

// portal

export const getPortalArticle = (param, stats) => getMultipaged('portal/article', param, stats, 30 * 60000);

// money

export const getMoneyCurrency = () => getCached('get', 'money/currency', null, 30 * 60000);

export const getMoneyPair = () => getCached('get', 'money/pair', null, 30 * 60000);

export const getMoneyCurrencyList = (visitorId) => getData(['money/currency/list', visitorId], null, 30 * 60000);

export const getMoneyCurrencyRate = (srcCode, dstCode) => cache.get(null, ['get', 'money/currency/rate', srcCode, dstCode], (context) => rest('get', 'money/currency/rate/' + srcCode + '-' + dstCode).then((data) => data[dstCode]), 30000);

export const getMoneyAccountList = () => getList(['money/account/list'], null);

export const getMoneyAccountBalance = (visitorId) => getList(['money/account/balance', visitorId], null, 5 * 60000);

export const clearMoneyAccountBalance = () => cache.clearByPrefix(null, ['money/account/balance']);

export const getMoneyTransmitComission = () => getCached('get', 'money/transmit/comission', null, 30 * 60000);

export const getMoneyTransmitDirection = () => getCached('get', 'money/transmit/direction', null, 30 * 60000);

export const getMoneyCard = (token) => getCached('get', 'money/card', { token }, 5 * 60000);

// shop

export const getShopGoods = (param, stats) => getMultipaged('shop/goods', param, stats, 30 * 60000);

export const getShopGoodsCategory = () => getCached('get', 'shop/goods/category', null, 60000);

export const getShopGoodsCategoryEx = async (visitorInfo) => {
  return getShopGoodsCategory()
    .then((data) => {
      let categories = data
        .filter((category) => Array.isArray(category.visible_place) && category.visible_place.indexOf('office') >= 0 && visitorInfo.inGroup(category.visitor_group))
        .map((category) => ({ ...category }));
      
      categories = categories.filter((category) => {
        if (category.parent) {
          category.parent = categories.find((item) => item.id === category.parent);
          
          if (!category.parent)
            return false;
          
          category.parent.hasChildren = true;
        }
        
        return true;
      });
      
      categories.forEach((category) => {
        category.path = [];
        
        let current = category.parent;
        
        while (current) {
          category.path.unshift(current);
          
          current = current.parent;
        }
        
        category.pathString = [...category.path, category].map((item) => item.name).join('/');
      });
      
      return categories;
    })
    .catch((error) => {
      console.error(error);
      
      return [];
    });
};

export const getShopGoodsPrice = () => getCached('get', 'shop/goods/price', null, 5 * 60000);

export const getShopMarker = (code) => rest('get', 'shop/marker', { code });

// delivery

export const getDeliveryOptions = (token) => getCached('get', 'delivery/options', { token }, 60 * 60000, { attemptCount: 1 });

export const putDeliveryOptions = (token, data) => rest('put', http.param('delivery/options', { token }), data, { attemptCount: 1 });

export const getDeliveryWarehouse = (type) => getCached('get', type ? 'delivery/warehouse/' + type : 'delivery/warehouse', null, 60000, { attemptCount: 1 });

export const getDeliveryGoodsRest = (id, warehouse) => cache.get(
    null,
    ['delivery/goods/rest', warehouse],
    (context, reduce) => rest('post', 'delivery/goods/rest', [[warehouse, reduce]], { attemptCount: 1 }).then((data) => data[0]?.reduce((result, value, index) => (result[reduce[index]] = value, result), {})),
    5 * 60000, // clearMillis
    id, // reduceKey
    100 // reduceMillis
  );

// office

export const getOfficeSettingsValues = () => getData(['office/settings/values'], null);

export const getOfficeSettingsValue = async (name, defaultValue) => {
  try {
    const result = (await getOfficeSettingsValues())[name];
    
    if (typeof result === 'undefined' || result === null)
      return typeof defaultValue === 'undefined' ? result : defaultValue;
    
    return result;
  } catch (e) {
    if (typeof defaultValue === 'undefined')
      throw e;
    
    return defaultValue;
  }
};

// report

export const getReportAchievementSection = () => getCached('get', 'report/achievement/section', {}, 30 * 60000);

export const getReportAchievementBlock = () => getCached('get', 'report/achievement/block', {}, 30 * 60000);

export const getReportAchievementColumn = () => getCached('get', 'report/achievement/column', {}, 30 * 60000);

export const getReportAchievement = (id) => getCached('get', 'report/achievement/' + id, {}, 10 * 60000);

// binary

export const getBinaryTree = (param) => getCached('get', 'binary/tree', param, 1 * 60000);

export const getBinaryTreeSearch = (param) => getCached('get', 'binary/tree/search', param, 1 * 60000);

export const clearBinaryTreeCache = () => cache.clearByPrefix(null, ['get', 'binary/tree']);

export const getBinaryDown = (id, direction) => getCached('get', 'binary/down', { id, direction }, 5 * 60000);

export const getBinaryLowest = (id, direction) => getCached('get', 'binary/lowest', { id, direction }, 5 * 60000);

export const getBinaryPoints = (param, stats) => getMultipaged('binary/points', param, stats, 30 * 60000);

export const setBinaryDirection = (id, token, data) => rest('put', 'binary/tree/' + id + '/create?token=' + token, data);

// ourclub

export const getOurclubVisitorMatrixZonePrograms = () => getList(['ourclub/visitor/matrix_zone/programs'], null);

//


const buildProps = (defaultProps, props) => {
  if (!props || typeof props !== 'object')
    return defaultProps;
  
  props = { ...defaultProps, ...props };
  
  PropTypes.checkPropTypes(propTypes, props, 'prop', 'API');
  
  return props;
};

/*const prepareErrors = async (lang, codes) => {
  if (!codes)
    return;
  
  let errors = null;
  
  for (const [key, error] of Object.entries(codes)) {
    if (!errors)
      errors = {};
    
    errors[key] = {
      code: error.code,
      message: langs.lineOr(lang, error.message, error.default_message)
    };
  }
  
  if (!errors)
    return;
  
  for (const error of Object.values(errors))
    error.message = await error.message;
  
  //console.log(errors);
  
  throw new ApiError('data-incorrect', 'Data incorrect', { errors });
};*/

function getError(json = {}) {
  let nestedError = null;

  // Проверяем, что есть data.errors и это объект
  if (json.data && json.data.errors && typeof json.data.errors === 'object') {
    const keys = Object.keys(json.data.errors);

    if (keys.length > 0) {
      const firstKey = keys[0];           // например, "value" или другое имя
      nestedError = json.data.errors[firstKey];
    }

    return {
      message1: json && json.message ? json.message : "",
      message2: nestedError && nestedError.message ? nestedError.message : "",
      message3: nestedError && nestedError.default_message ? nestedError.default_message : "",
    };
  } else {
    return {
      message1: json.message,
      message2: "",
      message3: "",
    };

  }


}

const fetchData = async (uri, param, props) => {
  props = buildProps(defaultProps, props);
  
  if (props.requestFromServer) {
    if (typeof Window !== 'undefined')
      throw new ApiError('network-error', 'Requests from the website are not allowed, uri: "' + uri + '"', null);
  } else {
    if (typeof Window === 'undefined')
      throw new ApiError('network-error', 'Requests from the server are not allowed, uri: "' + uri + '"', null);
  }
  
  let resp;
  
  if (props.requestDelay)
    await timeout(props.requestDelay);
  
  if (param.mode === 'cors' && props.requestServer)
    param.credentials = 'include';
  
  for (let count = props.attemptCount - 1; ; count--) {
    try {
      resp = await fetch(props.requestServer + uri, param);
      
      if (resp.status === 401) {
        throw new ApiError(
          API_ERROR.VISITOR_UNAUTHORIZED,
          'Session expired',
          null,
          {
            silentConsole: VERIFICATION_STATUS_PATTERN.test(uri),
            softUnauthorized: isSoftUnauthorized(uri)
          }
        );
      }
      
      if (resp.status !== 200) {
        if (!count)
          throw new ApiError('server-error', resp.status + ' ' + resp.statusText, null);
        
        await timeout(props.attemptInterval);
        
        continue;
      }
      
      break;
    } catch (e) {
      if (!count) {
        if (e instanceof ApiError)
          throw e;
        
        throw new ApiError('network-error', 'Error while fetching data at "' + uri + '": ' + e.message, null, { cause: e });
      }
      
      await timeout(props.attemptInterval);
    }
  }
  
  let json;
  
  try {
    json = await resp.json();
  } catch (e) {
    throw new ApiError('server-error', 'Invalid JSON format in response: ' + e.message);
  }
  
  if (props.expectedCode && json.code !== props.expectedCode) {
    if (json.code === API_ERROR.VISITOR_UNAUTHORIZED) {
      notify(API_NOTICE.VISITOR_LOGOUT, buildUnauthorizedNoticeData(uri));
      
      throw new ApiError(
        API_ERROR.VISITOR_UNAUTHORIZED,
        'Session expired',
        json.data,
        {
          silentConsole: VERIFICATION_STATUS_PATTERN.test(uri),
          softUnauthorized: isSoftUnauthorized(uri)
        }
      );
    }
    
    //if (json.code === 'data-incorrect')
    //  await prepareErrors(props.lang, json.data?.errors);

  /*  let error = json.message;
     if (json.code==="data-incorrect") {
         error = getError(json)
    }*/
    
    let error = getError(json);
    throw new ApiError(json.code, error, json.data);
  }
  
  if (json.code === API_ERROR.VISITOR_UNAUTHORIZED || json.code === 'visitor-unauthorized') {
    throw new ApiError(
      API_ERROR.VISITOR_UNAUTHORIZED,
      'Session expired',
      json.data,
      {
        silentConsole: VERIFICATION_STATUS_PATTERN.test(uri),
        softUnauthorized: isSoftUnauthorized(uri)
      }
    );
  }
  
  return json;
};

const getData = (key, param, clearMillis) => cache.get(
    null,
    key,
    (context) => request(key[0], param),
    clearMillis
  );

const getList = (key, param, clearMillis) => cache.get(
    null,
    key,
    (context) => request(key[0], param).then((data) => data.list || []),
    clearMillis
  );

if (typeof window !== 'undefined')
  window.addEventListener('notify', (event) => event.detail.name === 'money/visitor/balance' && clearMoneyAccountBalance());
